import type { ClientHomePlanHydratedNoDoc } from "api-types/homePlans/homePlanResponses";
import type { ClientLocationPlan, ClientLocationPlanListData } from "api-types/locationPlans/locationPlansResponses";
import {
  evaluateHomePlanCreationForLocationPlan,
  getHomePlanCreationBlockMessage,
  HOME_PLAN_CREATION_BLOCK_MESSAGES,
  type ClientHomePlanCreationEligibility,
  partitionCreatableLocationPlans,
} from "lib/components/builders/communities/plans/homePlanCreationRules";
import { getAllowedCommunityPlanIdsFromLot, type LotRowData } from "lib/state/LotsContext";
import { useMemo } from "react";

interface CreatableLocationPlansArgs {
  locationPlansData: ClientLocationPlanListData | undefined;
  lot?: LotRowData;
  homePlan?: ClientHomePlanHydratedNoDoc;
  locationPlan?: ClientLocationPlan;
}

interface CreatableLocationPlansResult {
  /** Plans offered at the location, narrowed to the lot's allowed plans when a lot is set. */
  availableLocationPlans: ClientLocationPlan[];
  /** Subset of available plans a home plan can actually be created from. */
  creatableLocationPlans: ClientLocationPlan[];
  /** Plan id → tooltip explaining why that plan cannot be selected. */
  disabledPlanReasons: Record<string, string>;
  /** Block message when creating against a pre-selected `locationPlan`, if any. */
  preselectedLocationPlanBlockMessage: string | undefined;
}

// Derives which location plans a home plan can be created from, and why the rest are blocked,
// for the Create/Update Home Plan modal
export function useCreatableLocationPlans({
  locationPlansData,
  lot,
  homePlan,
  locationPlan,
}: CreatableLocationPlansArgs): CreatableLocationPlansResult {
  return useMemo(() => {
    const allPlans = locationPlansData?.locationPlans ?? [];
    const masterPlans = locationPlansData?.masterPlans ?? [];
    const location = locationPlansData?.location;

    let availableLocationPlans = allPlans;
    if (lot) {
      const allowedIds = lot.allowedCommunityPlanIds ?? getAllowedCommunityPlanIdsFromLot(lot);
      availableLocationPlans = allPlans.filter((plan) => allowedIds.includes(plan.id));
    }

    const { creatable, blocked } = location
      ? partitionCreatableLocationPlans({ location, locationPlans: availableLocationPlans, masterPlans })
      : { creatable: availableLocationPlans, blocked: [] };

    const disabledPlanReasons: Record<string, string> = {};
    for (const { plan, reason } of blocked) {
      const message = HOME_PLAN_CREATION_BLOCK_MESSAGES[reason];
      if (message) {
        disabledPlanReasons[plan.id] = message;
      }
    }

    // for when plan is pre-selected like new home plan from lots table (!homePlan && locationPlan)
    // Used to determine a block message if that preselectedplan is not creatable
    const preselectedLocationPlanEligibility: ClientHomePlanCreationEligibility =
      !homePlan && locationPlan && location
        ? evaluateHomePlanCreationForLocationPlan({ location, locationPlan, masterPlans })
        : { ok: true };

    return {
      availableLocationPlans,
      creatableLocationPlans: creatable,
      disabledPlanReasons,
      preselectedLocationPlanBlockMessage: getHomePlanCreationBlockMessage(preselectedLocationPlanEligibility),
    };
  }, [locationPlansData, lot, homePlan, locationPlan]);
}
